import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageLayout from '../components/layout/PageLayout';
import {
  useLazyGetNotificationsListQuery,
  useMarkNotificationAsReadMutation,
} from '../store/api';
import { extractList, extractPagination } from '../utils/response';
import { getErrorMessage } from '../utils/errorHandler';

const formatTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const getNotificationIcon = (notificationType) => {
  switch (notificationType) {
    case 'meeting_scheduled':
      return <MeetingRoomIcon sx={{ color: '#154A8A', fontSize: 22 }} />;
    default:
      return <EmailIcon sx={{ color: '#154A8A', fontSize: 22 }} />;
  }
};

const isUnread = (n) => {
  if (typeof n?.is_read === 'boolean') return !n.is_read;
  return !n?.read_at;
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [fetchNotificationsList] = useLazyGetNotificationsListQuery();
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        paginate: 1,
        page: page + 1,
        per_page: rowsPerPage,
      };
      const res = await fetchNotificationsList(params).unwrap();
      const raw = extractList(res);
      const rows = Array.isArray(raw) ? raw : [];
      setList(rows);
      const pag = extractPagination(res);
      const t = Number(pag.total);
      setTotal(Number.isFinite(t) && t > 0 ? t : rows.length);
    } catch (err) {
      toast.error(err?.data?.message || getErrorMessage(err) || 'Failed to load notifications');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [fetchNotificationsList, page, rowsPerPage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleRowNavigate = async (notification) => {
    try {
      await markNotificationAsRead(notification.id).unwrap();
      setList((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n
        )
      );
      const url = notification.data?.url;
      if (url?.includes('/leads/')) {
        const parts = url.split('/');
        navigate(`/leads/view/${parts[parts.length - 1]}`);
      } else if (url?.includes('/issues/')) {
        const parts = url.split('/');
        navigate(`/issues/view/${parts[parts.length - 1]}`);
      }
    } catch (e) {
      toast.error(e?.data?.message || getErrorMessage(e) || 'Failed to open notification');
    }
  };

  const handleMarkRead = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await markNotificationAsRead(notificationId).unwrap();
      setList((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read_at: n.read_at || new Date().toISOString() } : n
        )
      );
      toast.success('Marked as read');
    } catch (err) {
      toast.error(err?.data?.message || getErrorMessage(err) || 'Failed to mark as read');
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#154A8A', mb: 3 }}>
          Notifications
        </Typography>

        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#154A8A' }} />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: { xs: '70vh', md: '65vh' } }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f5f5f5', fontWeight: 600 } }}>
                      <TableCell width={48} />
                      <TableCell>Title</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell width={120}>Status</TableCell>
                      <TableCell width={180}>Time</TableCell>
                      <TableCell width={56} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Typography color="text.secondary">No notifications yet.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      list.map((n) => (
                        <TableRow
                          key={n.id}
                          hover
                          sx={{
                            cursor: n.data?.url ? 'pointer' : 'default',
                            bgcolor: isUnread(n) ? 'action.hover' : 'transparent',
                          }}
                          onClick={() => {
                            if (n.data?.url) handleRowNavigate(n);
                          }}
                        >
                          <TableCell>{getNotificationIcon(n.data?.type)}</TableCell>
                          <TableCell sx={{ fontWeight: isUnread(n) ? 600 : 400 }}>
                            {n.data?.title || 'Notification'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 360 }}>
                            <Typography variant="body2" noWrap title={n.data?.message || ''}>
                              {n.data?.message || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isUnread(n) ? (
                              <Chip label="Unread" size="small" color="primary" variant="outlined" />
                            ) : (
                              <Chip label="Read" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>{formatTime(n.created_at)}</TableCell>
                          <TableCell align="right">
                            {isUnread(n) && (
                              <IconButton
                                size="small"
                                aria-label="Mark as read"
                                onClick={(e) => handleMarkRead(e, n.id)}
                              >
                                <MarkEmailReadIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {total > 0 && (
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              )}
            </>
          )}
        </Paper>
      </Container>
    </PageLayout>
  );
};

export default NotificationsPage;
